import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';

import { getGiftData } from '@/api/gifting/gifting';
import { PHOTO_EXPIRY_MARGIN_MS } from '@/constants';
import {
  useGiftImageExpiresAt,
  useGiftImageUrl,
  useGiftInviteCode,
  useHydrated,
  useSetGiftImage,
} from '@/store';

/**
 * Keeps the child's photo loadable.
 *
 * `photoSignedUrl` is a GCS pre-signed URL that dies an hour after it is
 * issued (`photoExpiresIn`), but it is persisted alongside the child's name —
 * and a name cannot go stale. Only the landing page fetches the gift, so
 * anyone who takes over an hour, or leaves a tab and comes back, lands on a
 * later step holding a dead URL. It is still a non-empty string, so the
 * avatar's initial-letter fallback never fires and a broken image renders.
 *
 * Mounted from the Layout, so it applies to every route.
 */
export default function useGiftPhotoRefresh() {
  const isHydrated = useHydrated();
  const code = useGiftInviteCode();
  const photoUrl = useGiftImageUrl();
  const expiresAt = useGiftImageExpiresAt();
  const setGiftImage = useSetGiftImage();

  // Decided once per mount rather than per render: reading the clock during
  // render is impure, and this only has to be answered when a page loads.
  //
  // A stored URL with no recorded expiry predates this and counts as dead —
  // that is precisely the state showing a broken image today.
  const [mountedAt] = useState(() => Date.now());
  const isExpired = (expiresAt ?? 0) - PHOTO_EXPIRY_MARGIN_MS <= mountedAt;
  const shouldRefresh = isHydrated && !!code && !!photoUrl && isExpired;

  const { data } = useQuery({
    queryKey: ['giftPhoto', code],
    queryFn: () => getGiftData({ code }),
    enabled: shouldRefresh,
    // The point is a URL newer than the dead one in hand.
    staleTime: 0,
    gcTime: 0,
  });

  useEffect(() => {
    const child = data?.data?.child;
    if (!child?.photoSignedUrl) return;
    setGiftImage(child.photoSignedUrl, child.photoExpiresIn);
  }, [data, setGiftImage]);
}
